import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  alle as ladeStaffeln,
  steuerSetzen,
  stufenSetzen,
  type Staffelzeile,
  type Stufe,
} from '../api/staffeln';
import { ApiFehler } from '../api/fehler';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Storno- und Umbuchungsstaffeln (BUC-45, BUC-142, BUC-145).
 *
 * ============================================================================
 * WARUM ES DIESE MASKE GEBEN MUSS
 * ============================================================================
 *
 * BUC-45: Die Werte „stammen aus § 6 und § 7 der Allgemeinen
 * Geschäftsbedingungen und sind OHNE PROGRAMMIERUNG ÄNDERBAR."
 *
 * Migration 0056 hat sie in den Bestand gebracht. Änderbar waren sie damit
 * nur per SQL — und das ist Programmierung. Erst mit dieser Maske ist die
 * Anforderung erfüllt.
 *
 * ============================================================================
 * „NICHT VORGESEHEN" IST KEIN SATZ VON NULL PROZENT
 * ============================================================================
 *
 * Beim Privatkunden im Standardtarif ist eine Umbuchung ausgeschlossen — ein
 * Terminwechsel läuft als Storno mit Neubuchung. Als „0 %" eingetragen wäre
 * sie KOSTENFREI statt unmöglich, und das ist der teuerste denkbare
 * Tippfehler dieser Maske.
 *
 * Die Zeile trägt deshalb ein Kästchen und kein Satzfeld: Wer „nicht
 * vorgesehen" ankreuzt, kann keinen Satz mehr eingeben. Ein Feld, das beides
 * annimmt, lädt zum Verwechseln ein.
 *
 * ============================================================================
 * DIE STAFFEL WIRD ALS GANZES GESPEICHERT
 * ============================================================================
 *
 * Zeilenweise gepflegt gäbe es zwischendurch immer einen Satz mit einer
 * Lücke — und in genau diesem Augenblick rechnet jemand eine Stornogebühr.
 * Dieselbe Festlegung wie beim Staffelpreis nach PRD-14.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Eine Zeile je Staffel: wofür sie gilt, ihre Stufen, der Steuerausweis
 * (er sichert beim Wählen, wie bisher). Die Segmente trennen nach Vorgang.
 * Die Stufen bearbeitet man im Fenster — die Staffel ist ein Ganzes und
 * wird als Ganzes gespeichert; eine Vorschau in der Seitenleiste, die beim
 * Überfahren der nächsten Zeile wechselte, verlöre einen halben Entwurf.
 */

const VORGANGSNAME: Readonly<Record<string, string>> = {
  storno: 'Storno',
  umbuchung: 'Umbuchung',
  teilnehmerwechsel: 'Teilnehmerwechsel',
};

/** Eine Stufe im Bearbeitungszustand — Sätze bleiben Text. */
type Zeile = {
  readonly abTagenVorTermin: string;
  readonly prozent: string;
  readonly nichtVorgesehen: boolean;
};

function alsZeilen(stufen: readonly Stufe[]): Zeile[] {
  return stufen.map((s) => ({
    abTagenVorTermin: String(s.abTagenVorTermin),
    prozent: s.prozent ?? '',
    nichtVorgesehen: s.nichtVorgesehen,
  }));
}

export function Gebuehrenstaffeln(): JSX.Element {
  const [staffeln, setzeStaffeln] = useState<readonly Staffelzeile[]>([]);
  const [offen, setzeOffen] = useState<string | null>(null);
  const [zeilen, setzeZeilen] = useState<Zeile[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laedt, setzeLaedt] = useState(true);
  /** Die Wahl der Segmente — nur eine Sicht, nichts wird gefiltert gespeichert. */
  const [vorgang, setzeVorgang] = useState<string>('alle');

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      setzeStaffeln(await ladeStaffeln());
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  const oeffnen = useCallback(
    (staffel: Staffelzeile) => {
      setzeMeldung(null);
      setzeFehler(null);
      if (offen === staffel.id) {
        setzeOffen(null);
        return;
      }
      setzeOffen(staffel.id);
      setzeZeilen(alsZeilen(staffel.stufen));
    },
    [offen],
  );

  const speichern = useCallback(
    async (staffel: string) => {
      setzeLaedt(true);
      setzeFehler(null);
      setzeMeldung(null);
      try {
        await stufenSetzen(
          staffel,
          zeilen.map((z) => ({
            abTagenVorTermin: Number(z.abTagenVorTermin),
            ...(z.nichtVorgesehen ? { nichtVorgesehen: true } : { prozent: z.prozent }),
          })),
        );
        setzeMeldung('Die Staffel ist gespeichert. Sie gilt ab dem nächsten Vorgang.');
        setzeOffen(null);
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [zeilen, holen],
  );

  const steuerAendern = useCallback(
    async (staffel: string, schluessel: string) => {
      setzeLaedt(true);
      setzeFehler(null);
      try {
        await steuerSetzen(staffel, schluessel);
        setzeMeldung('Der Steuerausweis ist geändert.');
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [holen],
  );

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  const gezeigt = vorgang === 'alle' ? staffeln : staffeln.filter((s) => s.vorgangsart === vorgang);
  useZaehler(laedt && staffeln.length === 0 ? undefined : zaehlwort(gezeigt.length, 'Staffel', 'Staffeln'));

  const bearbeitet = staffeln.find((s) => s.id === offen);

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      <Liste
        filter={
          <Segmente
            beschriftung="Vorgang"
            optionen={[
              { wert: 'alle', text: 'Alle' },
              ...Object.entries(VORGANGSNAME).map(([wert, text]) => ({ wert, text })),
            ]}
            gewaehlt={vorgang}
            beiWahl={setzeVorgang}
          />
        }
        hinweise={
          <p className="hilfe">
            Die Sätze stammen aus § 6 und § 7 der Geschäftsbedingungen. Sie sind hier änderbar,
            ohne dass etwas programmiert werden muss. Bereits abgerechnete Vorgänge bleiben
            unberührt — Satz und Stufe stehen dort als Werte im Beleg.
          </p>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">Storno- und Umbuchungsstaffeln</caption>
            <thead>
              <tr>
                <th scope="col">Staffel</th>
                <th scope="col">Gilt für</th>
                <th scope="col">Stufen</th>
                <th scope="col">Steuerausweis</th>
                <th scope="col">Handlung</th>
              </tr>
            </thead>
            <tbody>
              {gezeigt.map((s) => (
                <tr key={s.id}>
                  <th scope="row">
                    {s.bezeichnung}{' '}
                    <span className="marke">{s.kennung}</span>
                    {!s.aktiv && (
                      <>
                        {' '}
                        <span className="marke marke--warn">nicht aktiv</span>
                      </>
                    )}
                    {s.grundlage !== null && (
                      <>
                        <br />
                        <span className="hilfe">{s.grundlage}</span>
                      </>
                    )}
                  </th>
                  <td>
                    {s.kundenart === 'privat' ? 'Privatkunden' : 'Firmenkunden'}, {s.vertragsart},{' '}
                    {VORGANGSNAME[s.vorgangsart] ?? s.vorgangsart}
                    {s.artikel !== null && (
                      <>
                        {' '}
                        <span className="marke marke--warn">
                          nur für {s.artikelbezeichnung ?? 'ein Produkt'}
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    {/* Je Stufe eine Zeile: „ab 42 Tagen · 15 %". */}
                    {s.stufen.map((st) => (
                      <div key={st.abTagenVorTermin}>
                        ab {st.abTagenVorTermin} Tagen ·{' '}
                        {st.nichtVorgesehen ? (
                          <span className="marke marke--warn">nicht vorgesehen</span>
                        ) : (
                          <span>{`${Number(st.prozent ?? '0').toLocaleString('de-DE')} %`}</span>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    {/*
                     * Der Steuerausweis hängt an der AGB-Klausel: Eine
                     * Stornopauschale ist echter Schadensersatz und nicht steuerbar,
                     * ein Teilnehmerwechsel ist ein Bearbeitungsentgelt für eine
                     * erbrachte Leistung. Ändert sich die Klausel oder die
                     * Rechtslage, ändert sich hier eine Zeile (BUC-145).
                     */}
                    <select
                      className="feld__eingabe feld__eingabe--schmal"
                      aria-label={`Steuerausweis für ${s.kennung}`}
                      value={s.steuerschluessel}
                      disabled={laedt}
                      onChange={(e) => {
                        void steuerAendern(s.id, e.target.value);
                      }}
                    >
                      <option value="DE-NST">nicht steuerbar (Schadensersatz)</option>
                      <option value="DE-19">19 % Regelsatz</option>
                      <option value="DE-7">7 % ermäßigt</option>
                      <option value="DE-0">ohne Umsatzsteuer</option>
                    </select>
                    {s.steuerschluessel === 'DE-NST' && (
                      <p className="feld__hilfe">
                        Echter Schadensersatz — mangels Leistungsaustausch nicht steuerbar. Stünde
                        hier Umsatzsteuer, würde sie nach § 14c UStG geschuldet, allein weil sie auf
                        dem Beleg steht.
                      </p>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      onClick={() => {
                        oeffnen(s);
                      }}
                    >
                      Stufen ändern
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {gezeigt.length === 0 && !laedt && (
          <div className="liste__leer" role="status">
            {staffeln.length === 0
              ? 'Es ist keine Staffel hinterlegt.'
              : 'Für diesen Vorgang ist keine Staffel hinterlegt.'}
          </div>
        )}
      </Liste>

      {/*
        * DIE STUFEN IM FENSTER: Die Staffel wird als Ganzes gespeichert, und
        * das Fenster ist dieses Ganze — wer es schließt, hat nichts halb
        * gespeichert.
        */}
      {bearbeitet !== undefined && (
        <Dialog
          titel="Stufen ändern"
          unterzeile={`${bearbeitet.bezeichnung} · ${bearbeitet.kennung}`}
          breite="mittel"
          beiSchliessen={() => {
            setzeOffen(null);
          }}
        >
          {/* FRS-19 */}
          <p className="hilfe">
            Die Schwelle ist eine Untergrenze <strong>einschließlich</strong>: „ab 42 Tagen"
            gilt bei genau 42 Tagen Abstand noch. Eine Stufe ab 0 Tagen ist Pflicht —
            ohne sie fände ein Storno am Kurstag keine Stufe und bliebe kostenfrei.
          </p>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">Stufen bearbeiten</caption>
              <thead>
                <tr>
                  <th scope="col">ab Tagen</th>
                  <th scope="col">Satz in Prozent</th>
                  <th scope="col">nicht vorgesehen</th>
                  <th scope="col"> </th>
                </tr>
              </thead>
              <tbody>
                {/*
                  * Der Schlüssel ist die Stelle in der Liste. Die Zeilen
                  * haben vor dem Speichern keine Kennung, und ihre Schwelle
                  * ist es auch nicht: Sie wird gerade getippt und ist
                  * zwischendurch leer oder doppelt. Ihre Stelle IST hier die
                  * Identität.
                  */}
                {zeilen.map((z, i) => (
                  <tr key={`zeile-${String(i)}`}>
                    <td>
                      <input
                        type="number"
                        min={0}
                        className="feld__eingabe feld__eingabe--schmal"
                        aria-label={`Schwelle Zeile ${String(i + 1)}`}
                        value={z.abTagenVorTermin}
                        onChange={(e) => {
                          setzeZeilen(
                            zeilen.map((a, j) =>
                              j === i ? { ...a, abTagenVorTermin: e.target.value } : a,
                            ),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <input
                        className="feld__eingabe feld__eingabe--schmal"
                        aria-label={`Satz Zeile ${String(i + 1)}`}
                        value={z.prozent}
                        /*
                         * Wer "nicht vorgesehen" ankreuzt, kann keinen Satz
                         * mehr eingeben. Ein Feld, das beides annimmt, lädt
                         * zum Verwechseln ein — und "0 %" statt "nicht
                         * vorgesehen" macht aus unmöglich kostenfrei.
                         */
                        disabled={z.nichtVorgesehen}
                        onChange={(e) => {
                          setzeZeilen(
                            zeilen.map((a, j) => (j === i ? { ...a, prozent: e.target.value } : a)),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Nicht vorgesehen Zeile ${String(i + 1)}`}
                        checked={z.nichtVorgesehen}
                        onChange={(e) => {
                          setzeZeilen(
                            zeilen.map((a, j) =>
                              j === i
                                ? { ...a, nichtVorgesehen: e.target.checked, prozent: '' }
                                : a,
                            ),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        onClick={() => {
                          setzeZeilen(zeilen.filter((_, j) => j !== i));
                        }}
                      >
                        Entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Die Stufe gehört zur Tabelle darüber, nicht zum Abschluss (CRM-82). */}
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeZeilen([
                  ...zeilen,
                  { abTagenVorTermin: '0', prozent: '', nichtVorgesehen: false },
                ]);
              }}
            >
              Stufe hinzufügen
            </button>
          </div>
          <Abschlussreihe
            abbruch={
              <button
                type="button"
                className="knopf"
                onClick={() => {
                  setzeOffen(null);
                }}
              >
                Abbrechen
              </button>
            }
          >
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laedt || zeilen.length === 0}
              onClick={() => {
                void speichern(bearbeitet.id);
              }}
            >
              Staffel speichern
            </button>
          </Abschlussreihe>
        </Dialog>
      )}
    </>
  );
}
